// Component for SideBar Actions
import React from 'react';

import { Button, Grid, Paper, Typography } from '@mui/material';
import { theme } from '../../theme/theme';
import { useDispatch } from "react-redux";

import moment from 'moment'

import { usePostMetadataQuery,
  usePostArchiveQuery,
  usePostSoftwareUpdateCmdQuery, 
  usePostContentUpdateCmdQuery, 
  usePostRebootCmdQuery,
  usePostGetSyslogCmdQuery,
  apiDownloadSyslog,
  usePostCancelCmdQuery } from "./apiCloudSystem";
import { uncommissionedSummariesSlice } from "../../features/dashboard/apiDashboard";

import FileDownloadIcon from '@mui/icons-material/FileDownload';

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarActionsProps {
//   cloudSystemDetails : CloudSystem
//   onCommissionClicked : func
//   onNeedsGet : func
// }

const SideBarActions = (props) => {
  const [cloudSystemForUpdate, setCloudSystemForUpdate] = React.useState(null);
  const [needsPost, setNeedsPost] = React.useState(false);
  const [serialForArchive, setSerialForArchive] = React.useState(null);
  const [needsArchive, setNeedsArchive] = React.useState(false);
  const [serialForSoftwareUpdate, setSerialForSoftwareUpdate] = React.useState(null);
  const [needsSoftwareUpdate, setNeedsSoftwareUpdate] = React.useState(false);
  const [serialForContentUpdate, setSerialForContentUpdate] = React.useState(null);
  const [needsContentUpdate, setNeedsContentUpdate] = React.useState(false);
  const [serialForReboot, setSerialForReboot] = React.useState(null);
  const [needsReboot, setNeedsReboot] = React.useState(false);
  const [serialForGetSyslog, setSerialForGetSyslog] = React.useState(null);
  const [needsGetSyslog, setNeedsGetSyslog] = React.useState(false);
  const [serialForCancel, setSerialForCancel] = React.useState(null);
  const [needsCancel, setNeedsCancel] = React.useState(false);

  const dispatch = useDispatch();

  const {
    data: didUpdate,
    isError: isUpdateError,
    error: updateError,
  } = usePostMetadataQuery(cloudSystemForUpdate, { refetchOnMountOrArgChange: true, skip: !needsPost });

  const {
    isSuccess: didArchive,
    isError: isArchiveError,
    error: archiveError,
  } = usePostArchiveQuery(serialForArchive, { refetchOnMountOrArgChange: true, skip: !needsArchive });

  const {
    data: didSoftwareUpdate,
    isError: isSoftwareUpdateError,
    error: softwareUpdateError,
  } = usePostSoftwareUpdateCmdQuery(serialForSoftwareUpdate, { refetchOnMountOrArgChange: true, skip: !needsSoftwareUpdate });

  const {
    data: didContentUpdate,
    isError: isContentUpdateError,
    error: contentUpdateError,
  } = usePostContentUpdateCmdQuery(serialForContentUpdate, { refetchOnMountOrArgChange: true, skip: !needsContentUpdate });

  const {
    data: didReboot,
    isError: isRebootError,
    error: rebootError,
  } = usePostRebootCmdQuery(serialForReboot, { refetchOnMountOrArgChange: true, skip: !needsReboot });

  const {
    data: didGetSyslog,
    isError: isGetSyslogError,
    error: getSyslogError,
  } = usePostGetSyslogCmdQuery(serialForGetSyslog, { refetchOnMountOrArgChange: true, skip: !needsGetSyslog });

  const {
    data: didCancel,
    isError: isCancelError,
    error: cancelError,
  } = usePostCancelCmdQuery(serialForCancel, { refetchOnMountOrArgChange: true, skip: !needsCancel });

  React.useEffect(() => {
    if (isUpdateError) {
      console.error(`Error updating metadata: ${updateError}`);
    }
  }, [isUpdateError, updateError]);

  React.useEffect(() => {
    if (isArchiveError) {
      console.error(`Error archiving cloud system: ${archiveError}`);
    }
  }, [isArchiveError, archiveError]);

  React.useEffect(() => {
    if (isSoftwareUpdateError) {
      console.error(`Error sending Software Update command: ${softwareUpdateError}`);
    }
  }, [isSoftwareUpdateError, softwareUpdateError]);

  React.useEffect(() => {
    if (isContentUpdateError) {
      console.error(`Error sending Content Update command: ${contentUpdateError}`);
    }
  }, [isContentUpdateError, contentUpdateError]);

  React.useEffect(() => {
    if (isRebootError) {
      console.error(`Error sending Reboot command: ${rebootError}`);
    }
  }, [isRebootError, rebootError]);

  React.useEffect(() => {
    if (isGetSyslogError) {
      console.error(`Error sending GetSyslog command: ${getSyslogError}`);
    }
  }, [isGetSyslogError, getSyslogError]);

  React.useEffect(() => {
    if (isCancelError) {
      console.error(`Error sending Cancel command: ${cancelError}`);
    }
  }, [isCancelError, cancelError]);

  React.useEffect(() => {
    if (didUpdate || didReboot || didCancel || didSoftwareUpdate || didContentUpdate || didGetSyslog) {
      // Trigger GET immediately
      if (props.onNeedsGet) {
        props.onNeedsGet()
      }
    }
  }, [didUpdate, didReboot, didCancel, didSoftwareUpdate, didContentUpdate, didGetSyslog]);

  React.useEffect(() => {
    if (didArchive) {
      // Close the sidebar
      if (props.onClose) {
        props.onNeedsGet(true)
        props.onClose()
      }
    }
  }, [didArchive])

  /* Click Handlers */

  const handleDecommissionClicked = () => {
    if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
      var forUpdate = {
        "serialNumber": props.cloudSystemDetails.serialNumber,
        "isCommissioned": false,
      };
      setCloudSystemForUpdate(forUpdate);
      setNeedsPost(true);
      dispatch(uncommissionedSummariesSlice.actions.increment());
    } else {
      console.error("Error setting CloudSystem to be de-commissioned");
    }
  }

  const handleArchiveClicked = () => {
    if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
      var forArchive = {
        serialNumber: props.cloudSystemDetails.serialNumber,
        trigger: serialForArchive?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
      };
      setSerialForArchive(forArchive);
      setNeedsArchive(true);
    } else {
      console.error("Error archiving CloudSystem");
    }
  }

  const handleTriggerSoftwareUpdateClicked = () => {
    if (["Unsent","WaitingForAck"].includes(props.cloudSystemDetails?.actions?.find(a => a.method === "update-software")?.status)) {
      // Cancel Software Update case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forCancel = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          commandActionID: props.cloudSystemDetails?.actions?.find(a => a.method === "update-software")?.commandActionID,
          trigger: serialForCancel?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForCancel(forCancel);
        setNeedsCancel(true);
      } else {
        console.error("Error setting data to cancel software update");
      }
    } else {
      // Software Update case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forSoftwareUpdate = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          trigger: serialForSoftwareUpdate?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForSoftwareUpdate(forSoftwareUpdate);
        setNeedsSoftwareUpdate(true);
      } else {
        console.error("Error setting CloudSystem serial to have software updated");
      }
    }
  }

  const handleTriggerContentUpdateClicked = () => {
    if (["Unsent","WaitingForAck"].includes(props.cloudSystemDetails?.actions?.find(a => a.method === "update-content")?.status)) {
      // Cancel Content Update case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forCancel = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          commandActionID: props.cloudSystemDetails?.actions?.find(a => a.method === "update-content")?.commandActionID,
          trigger: serialForCancel?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForCancel(forCancel);
        setNeedsCancel(true);
      } else {
        console.error("Error setting data to cancel content update");
      }
    } else {
      // Content Update case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forContentUpdate = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          trigger: serialForContentUpdate?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForContentUpdate(forContentUpdate);
        setNeedsContentUpdate(true);
      } else {
        console.error("Error setting CloudSystem serial to have content updated");
      }
    }
  }

  const handleRebootClicked = () => {
    if (["Unsent","WaitingForAck"].includes(props.cloudSystemDetails?.actions?.find(a => a.method === "reboot")?.status)) {
      // Cancel Reboot case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forCancel = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          commandActionID: props.cloudSystemDetails?.actions?.find(a => a.method === "reboot")?.commandActionID,
          trigger: serialForCancel?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForCancel(forCancel);
        setNeedsCancel(true);
      } else {
        console.error("Error setting data to cancel reboot");
      }
    } else {
      // Reboot case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forReboot = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          trigger: serialForReboot?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForReboot(forReboot);
        setNeedsReboot(true);
      } else {
        console.error("Error setting CloudSystem serial to be rebooted");
      }
    }
  }

  const handleGetSyslogClicked = () => {
    if (["Unsent","WaitingForAck"].includes(props.cloudSystemDetails?.actions?.find(a => a.method === "get-syslog")?.status)) {
      // Cancel GetSyslog case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forCancel = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          commandActionID: props.cloudSystemDetails?.actions?.find(a => a.method === "get-syslog")?.commandActionID,
          trigger: serialForCancel?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForCancel(forCancel);
        setNeedsCancel(true);
      } else {
        console.error("Error setting data to cancel get-syslog");
      }
    } else {
      // GetSyslog case
      if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
        var forGetSyslog = {
          serialNumber: props.cloudSystemDetails.serialNumber,
          trigger: serialForGetSyslog?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
        };
  
        setSerialForGetSyslog(forGetSyslog);
        setNeedsGetSyslog(true);
      } else {
        console.error("Error setting CloudSystem serial for GetSyslog");
      }
    }
  }

  const handleDownloadSyslogClicked = () => {
    if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
      apiDownloadSyslog(props.cloudSystemDetails.serialNumber)
    } else {
      console.error("Error setting CloudSystem serial for DownloadSyslog");
    }
  }

  /* Helpers */

  const convertTimestamp = (datetime) => {
    if (datetime) {
      return moment(datetime).format("MM/DD/YY hh:mm:ss a")
    } else {
      return "-- --"
    }
  }

  const renderStatusFromState = (state) => {
    switch (state) {
      case "Unsent":
      case "WaitingForAck":
        return "Command Queued";
      case "Acknowledged":
        return "Command Accepted";
      case "Finished":
        return "Command Completed";
      case "Failed":
        return "Command Rejected";
      case "Cancelled":
        return "Command Cancelled";
      default:
        return "-- --"
    }
  }

  const getButtonParamsFromState = (state) => {
    var params = {
      "text": "",
      "disabled": false,
      "backgroundColor": undefined,
    };

    switch (state) {
      case "Unsent":
      case "WaitingForAck":
        // Cancel && enabled
        params.text = "Cancel";
        params.disabled = false;
        params.backgroundColor = theme.palette.sidebarContent.cancel;
        break;
      case "Acknowledged":
        // Cancel && disabled
        params.text = "Cancel";
        params.disabled = true;
        params.backgroundColor = theme.palette.sidebarContent.disabled;
        break;
      case "Finished":
      case "Failed":
      case "Cancelled":
      default:
        // Send Cmd && enabled
        params.text = "Send CMD";
        params.disabled = false;
        params.backgroundColor = theme.palette.tertiary.main;
        break;
    }

    return params;
  }

  const getContainerParamsFromState = (state) => {
    var params = {
      "border": `1px solid ${theme.palette.tertiary.main}`,
      "backgroundColor": theme.palette.sidebarContent.main,
    };

    switch (state) {
      case "Unsent":
      case "WaitingForAck":
      case "Acknowledged":
        break;
      case "Finished":
        params.border = `1px solid ${theme.palette.success.main}`;
        params.backgroundColor = theme.palette.success.withOpacity;
        break;
      case "Failed":
        params.border = `1px solid ${theme.palette.error.main}`;
        params.backgroundColor = theme.palette.error.withOpacity;
        break;
      case "Cancelled":
        params.border = `1px solid ${theme.palette.error.main}`;
        params.backgroundColor = theme.palette.error.withOpacity;
        break;
      default:
        // Default to Unsent state
        break;
    }

    return params;
  }

  // Generate details view row from single [label, value] pair
  const createCommandDetailsRow = (key, value, index) => {
    return (
      <Grid key={index} container item direction={'row'}>
        <Grid item>
          <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
            {key}
          </Typography>
        </Grid>
        <Grid sx={{ marginLeft: 'auto' }}>
          <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
            {/* Index 0 means status row */}
            {index === 0 ? renderStatusFromState(value) : value ?? "-- --"}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  // Generate a uniform details view from [label, value] array 
  const createCommandDetailsContainer = (keyValuePairs, actionStatus) => {
    // Apply styling to this container based on command state
    var styling = getContainerParamsFromState(actionStatus);
    return (
      <Grid container item sx={{ border: styling.border, backgroundColor: styling.backgroundColor, borderRadius: '4px', p: 2, my: 2 }}>
        {keyValuePairs.map((keyValuePair, index) => {
          return createCommandDetailsRow(keyValuePair[0], keyValuePair[1], index);
        })}
      </Grid>
    );
  }

  const createCommandActionSection = (actionText, actionCallback, actionObject) => {
    var btnParams = getButtonParamsFromState(actionObject?.status);

    return (
    <Grid container direction={'row'} alignItems='center' sx={{ borderTop: '1 solid #707070' }}>
      <Grid item>
        <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
          {actionText}
        </Typography>
      </Grid>
      <Grid item sx={{ marginLeft: "auto" }}>
        <Button 
          onClick={actionCallback} 
          sx={{ 
            color: theme.palette.tertiary.contrastText, 
            backgroundColor: btnParams.backgroundColor,
            width: '7em',
          }} 
          disabled={btnParams.disabled
        }>
          {btnParams.text}
        </Button>
      </Grid>
      {createCommandDetailsContainer([['Status', actionObject?.status], ['Timestamp', convertTimestamp(actionObject?.updatedAt)]], actionObject?.status)}
      {actionObject?.method === "get-syslog" && actionObject?.status === 'Finished' && (
        <Grid item xs={12}>
          <Button 
            onClick={handleDownloadSyslogClicked} 
            fullWidth
            sx={{ 
              color: theme.palette.tertiary.contrastText, 
              backgroundColor: theme.palette.tertiary.main,
            }}
          >
            Download Syslog
            <Grid item display='flex' alignItems='center' justifyContent='center' sx={{ ml: 'auto' }}>
              <FileDownloadIcon /> 
            </Grid>
          </Button>
        </Grid>
      )}
    </Grid>);
  }

  return (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.primary.main
        }}
    >
        {props.cloudSystemDetails.isCommissioned && props.cloudSystemDetails.isDashboardEnabled && (
          <>
            {createCommandActionSection("Trigger Software Update", handleTriggerSoftwareUpdateClicked, props.cloudSystemDetails?.actions?.find(a => a.method === "update-software"))}
            {createCommandActionSection("Trigger Content Update", handleTriggerContentUpdateClicked, props.cloudSystemDetails?.actions?.find(a => a.method === "update-content"))}
            {createCommandActionSection("Reboot Device", handleRebootClicked, props.cloudSystemDetails?.actions?.find(a => a.method === "reboot"))}
            {createCommandActionSection("Get Syslog", handleGetSyslogClicked, props.cloudSystemDetails?.actions?.find(a => a.method === "get-syslog"))}
          </>
        )}

        {props.cloudSystemDetails.isCommissioned && (
          <>
            <Grid container direction="row" alignItems={'center'} sx={{ marginTop: 2, borderTop: '1 solid #707070' }}>
              <Grid item>
                <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
                  Move to Decommissioned
                </Typography>
              </Grid>
              <Grid item sx={{ marginLeft: "auto" }}>
                <Button onClick={handleDecommissionClicked} sx={{
                    color: theme.palette.tertiary.contrastText,
                    backgroundColor: theme.palette.tertiary.main,
                    width: '7em',
                }}>
                  Move
                </Button>
              </Grid>
            </Grid>
            <Grid container direction="row" alignItems={'center'} sx={{ marginTop: 2, borderTop: '1 solid #707070' }}>
              <Grid item>
                <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
                  Archive Cloud System
                </Typography>
              </Grid>
              <Grid item sx={{ marginLeft: "auto" }}>
                <Button onClick={handleArchiveClicked} sx={{
                    color: theme.palette.tertiary.contrastText,
                    backgroundColor: theme.palette.tertiary.main,
                    width: '7em',
                }}>
                  Archive
                </Button>
              </Grid>
            </Grid>
          </>
        )}
    </Paper>
  );
};

export default SideBarActions;